import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Waitlist = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [serverMsg, setServerMsg] = useState("");

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "Please tell us your name.";
    else if (name.trim().length > 80) next.name = "Name is too long.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "That doesn't look like a valid email.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) return;

    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/waitlist/signup`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      setServerMsg(data?.message || "You're on the list.");
      setStatus("success");
      toast.success("You're on the list.", {
        description: data?.email_sent
          ? "We just sent a confirmation to your inbox."
          : "We'll email you the moment early access opens.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again.";
      toast.error("Could not join waitlist", { description: msg });
      setStatus("idle");
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="bg-jade-felt text-[#F5F0E8] relative overflow-hidden"
    >
      {/* Decorative 中 watermark */}
      <div
        aria-hidden="true"
        className="absolute -left-10 -bottom-20 opacity-[0.08] pointer-events-none select-none font-han"
        style={{ fontSize: 420, lineHeight: 0.8, color: "#F5F0E8" }}
      >
        中
      </div>

      <div className="relative max-w-3xl mx-auto px-5 md:px-8 py-20 md:py-32 text-center">
        <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] text-[#F5F0E8]/70 mb-5">
          Early Access
        </p>
        <h2
          data-testid="waitlist-headline"
          className="text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight"
        >
          <span className="font-black">Reserve</span>{" "}
          <span className="font-light">your spot.</span>
        </h2>
        <p
          data-testid="waitlist-subheadline"
          className="mt-5 md:mt-6 text-base md:text-lg text-[#F5F0E8]/85 leading-relaxed max-w-xl mx-auto"
        >
          Free to join. Be first to play when we launch.
        </p>

        {status === "success" ? (
          <div
            data-testid="waitlist-success"
            className="mt-10 md:mt-14 max-w-lg mx-auto bg-[#F5F0E8] text-[#1A1A1A] rounded-sm p-8 md:p-10 text-left border border-[#F5F0E8]"
          >
            <div className="flex items-center gap-3 mb-4">
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#3D7A5A] text-[#F5F0E8] font-han text-xl"
                aria-hidden="true"
              >
                中
              </span>
              <span className="text-[11px] uppercase tracking-[0.25em] text-[#3D7A5A] font-bold">
                Confirmed
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl tracking-tight">
              <span className="font-black">You're in, </span>
              <span className="font-light">{name.split(" ")[0]}.</span>
            </h3>
            <p className="mt-3 text-[15px] md:text-base text-[#3a3a38] leading-relaxed">
              {serverMsg} Keep an eye on{" "}
              <span className="font-medium text-[#1A1A1A]">{email}</span>.
            </p>
            <button
              type="button"
              onClick={() => {
                setName("");
                setEmail("");
                setErrors({});
                setStatus("idle");
              }}
              data-testid="waitlist-add-another-btn"
              className="mt-6 text-sm font-bold text-[#3D7A5A] underline underline-offset-4 hover:text-[#2E5C44]"
            >
              Add another email →
            </button>
          </div>
        ) : (
          <form
            data-testid="waitlist-form"
            onSubmit={onSubmit}
            noValidate
            className="mt-10 md:mt-14 max-w-2xl mx-auto text-left"
          >
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <label
                  htmlFor="waitlist-name"
                  className="block text-[11px] uppercase tracking-[0.22em] text-[#F5F0E8]/70 mb-2"
                >
                  Name
                </label>
                <input
                  id="waitlist-name"
                  data-testid="waitlist-name-input"
                  type="text"
                  autoComplete="name"
                  className="jade-input"
                  placeholder="Chan Tai Man"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors({ ...errors, name: undefined });
                  }}
                  disabled={status === "submitting"}
                />
                {errors.name && (
                  <p
                    data-testid="waitlist-name-error"
                    className="mt-2 text-xs text-[#F5F0E8] bg-[#C0392B] inline-block px-2 py-1 rounded-sm"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="waitlist-email"
                  className="block text-[11px] uppercase tracking-[0.22em] text-[#F5F0E8]/70 mb-2"
                >
                  Email
                </label>
                <input
                  id="waitlist-email"
                  data-testid="waitlist-email-input"
                  type="email"
                  autoComplete="email"
                  className="jade-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: undefined });
                  }}
                  disabled={status === "submitting"}
                />
                {errors.email && (
                  <p
                    data-testid="waitlist-email-error"
                    className="mt-2 text-xs text-[#F5F0E8] bg-[#C0392B] inline-block px-2 py-1 rounded-sm"
                  >
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-10 flex flex-col items-center gap-4">
              <button
                type="submit"
                data-testid="waitlist-submit-btn"
                disabled={status === "submitting"}
                className="tile-shine bg-[#C0392B] hover:bg-[#9A2D22] disabled:opacity-60 disabled:cursor-not-allowed text-[#F5F0E8] font-bold tracking-wide px-8 py-4 rounded-sm transition-colors text-sm md:text-base min-w-[220px]"
              >
                {status === "submitting" ? "Joining…" : "Get Early Access"}
              </button>
              <p className="text-xs md:text-sm text-[#F5F0E8]/70">
                No spam. Just your spot in line. Unsubscribe anytime.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default Waitlist;
